import { Chat, ChatResponseModeEnum, ChatRoleEnum } from 'types/chat'
import { getPlainMessageText } from 'utils/citations'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { chatIsToRead, findParticipantsByRole, getChatDateFormatted } from 'utils/chats'
import { useDispatch, useSelector } from 'react-redux'
import { messagesActions } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { markChatAsRead } from 'api/chats'
import { generateSrcFromEmail } from 'utils/avatar'
import { useGetSetting } from 'api/settings'
import { clsx } from 'clsx'

export type ChatRowProps = {
  chat: Chat
  selected?: boolean
}

export const ChatRow = ({ chat, selected = false }: ChatRowProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const currentUser = useSelector(settingsSelectors.userDetails)
  const { colors } = useGetSetting()
  const dateTime = useMemo(() => getChatDateFormatted(chat), [chat])

  const onClickChat = useCallback(async () => {
    dispatch(messagesActions.setChatId(chat._id))
    try {
      await markChatAsRead(chat._id)
    } catch (e) {
      console.error({ e })
    }
  }, [chat, dispatch])

  const isToRead = useMemo(() => {
    return chatIsToRead(chat, currentUser)
  }, [chat, currentUser])

  const lastMessagePreview = useMemo(() => {
    return getPlainMessageText(chat.lastMessage?.message ?? '')
  }, [chat.lastMessage?.message])

  const chatImageUrl = useMemo(() => {
    if (chat.responseMode === ChatResponseModeEnum.BOT) {
      return generateSrcFromEmail('bot@bot.com', colors.primary)
    }
    const operator = findParticipantsByRole(chat, ChatRoleEnum.OPERATOR)
    return generateSrcFromEmail(operator?.user?.email)
  }, [chat, colors.primary])

  const primary = chat.name?.trim() || lastMessagePreview || chat.ticket?.title || t('chat')

  return (
    <button
      type="button"
      onClick={onClickChat}
      aria-current={selected ? 'true' : undefined}
      className={clsx(
        'tw-flex tw-w-full tw-flex-row tw-items-center tw-gap-2.5 tw-border-0 tw-px-2 tw-py-2.5 tw-text-left tw-rounded-xl tw-cursor-pointer',
        'tw-transition-colors tw-duration-150 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:-tw-outline-offset-2',
        selected
          ? 'tw-bg-[color-mix(in_srgb,var(--brand-text)_14%,transparent)]'
          : 'tw-bg-transparent hover:tw-bg-[var(--input-bg)]',
      )}
    >
      <img
        src={chatImageUrl}
        alt={chat.responseMode}
        className={'tw-h-8 tw-w-8 tw-shrink-0 tw-rounded-full tw-bg-neutral-200'}
      />

      <div className={'tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0 tw-flex-1'}>
        <div className={'tw-flex tw-items-center tw-justify-between tw-gap-2'}>
          <div
            className={clsx('tw-truncate tw-text-sm', isToRead ? 'tw-font-bold' : 'tw-font-medium')}
            style={{ color: 'var(--text-primary)' }}
          >
            {primary}
          </div>
          {isToRead && (
            <div
              className={'tw-h-2.5 tw-w-2.5 tw-shrink-0 tw-rounded-full'}
              style={{ backgroundColor: 'var(--brand-text)' }}
            />
          )}
        </div>

        <div className={'tw-truncate tw-text-xs'} style={{ color: 'var(--text-tertiary)' }}>
          {dateTime}
        </div>
      </div>
    </button>
  )
}
